import { Button, Dialog, Flex, Portal, Separator, Text } from '@chakra-ui/react';
import { PredictionPosition } from '@features/positions/predictions/types';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { memo, useCallback } from 'react';

interface Props {
  position: PredictionPosition | null;
  open: boolean;
  onClose: () => void;
}

const PositionDetailDialog = ({ position, open, onClose }: Props) => {
  const { predictionMarketHref } = useHref();
  const router = useRouter();

  const onClickViewMarket = useCallback(() => {
    if (position == null) {
      return;
    }
    const href = predictionMarketHref(position.marketId);
    router.push(href);
    onClose();
  }, [position, predictionMarketHref, router, onClose]);

  if (position == null) {
    return null;
  }

  const priceChange = position.currentPrice - position.avgPrice;
  const isPositive = priceChange >= 0;

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()} size="sm">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Dialog.Title fontSize="lg">{position.market}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} py={4}>
              <Flex direction="column" gap={4}>
                <Flex direction="column" gap={1}>
                  <Text fontSize="sm" color="fg.subtle" fontWeight="medium">
                    Your prediction
                  </Text>
                  <Text fontSize="md" fontWeight="semibold">
                    {position.outcome}
                  </Text>
                </Flex>

                <Separator borderColor="border.light" />

                <Flex gap={6} flexWrap="wrap">
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Buy price
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {position.avgPrice.toFixed(2)}
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Current price
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {position.currentPrice.toFixed(2)}{' '}
                      <Text as="span" color={isPositive ? 'green.600' : 'red.600'}>
                        ({isPositive ? '+' : ''}
                        {priceChange.toFixed(2)})
                      </Text>
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Shares traded
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {position.traded}
                    </Text>
                  </Flex>
                </Flex>

                <Separator borderColor="border.light" />

                <Flex gap={6} flexWrap="wrap">
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Current value
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      ${position.value.toFixed(2)}
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      If correct
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="green.600">
                      ${position.toWin.toFixed(2)}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Dialog.Body>
            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button variant="outline" size="sm" borderRadius="md" onClick={onClose}>
                Close
              </Button>
              <Button size="sm" borderRadius="md" onClick={onClickViewMarket}>
                View market
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default memo(PositionDetailDialog);
